import { ColumnDef } from '@tanstack/react-table'
import { AppTable, AppTableProps, AppTypography, EmailText, TableActions, TextWithToolTip } from 'components'
import { SubAccountBillingStatus, SubAccountStatus } from 'globals/constants'
import { getFullDate, getMDYFormattedDate } from 'globals/functions'
import { useMembershipHelper } from 'hooks'
import { FC } from 'react'
import { SubAccountViewModel } from 'sdk'

interface Props extends AppTableProps<SubAccountViewModel> {
  onManageClick: (subAccount: SubAccountViewModel) => void
  onResendInvitationClick: (subAccountId: string) => void
  onAddUserBackToBilling: (subAccount: SubAccountViewModel) => void
}

export const SubAccountsTable: FC<Props> = ({
  onManageClick,
  onAddUserBackToBilling,
  onResendInvitationClick,
  ...props
}) => {
  const { CurrentMembership, IsPausedUser } = useMembershipHelper()
  const columns: ColumnDef<SubAccountViewModel>[] = [
    {
      id: 'popover',
      cell: ({ row }) => {
        const actions = [{ text: 'Manage', onClick: () => onManageClick(row.original) }]
        if (
          row.original.Status === SubAccountStatus.Pending &&
          row.original.BillingStatus !== SubAccountBillingStatus.Removed &&
          !!CurrentMembership()
        ) {
          actions.push({ text: 'Resend Invitation', onClick: () => onResendInvitationClick(row.original.Id) })
        }
        if (row.original.BillingStatus !== SubAccountBillingStatus.Billable && !!CurrentMembership()) {
          actions.push({ text: 'Re-Add To Billing', onClick: () => onAddUserBackToBilling(row.original) })
        }
        return <TableActions row={row} actions={actions} />
      },
    },
    {
      header: 'Status',
      cell: ({ row }) => (
        <>
          {!row.original.Active ? (
            <TextWithToolTip tooltipText={'Disabled By System. Contact support@parksites.io for further information'}>
              Disabled
            </TextWithToolTip>
          ) : SubAccountBillingStatus.Subscription_Removed === row.original.BillingStatus ? (
            <AppTypography color={'grey.500'}>{IsPausedUser() ? 'Paused' : 'Removed'}</AppTypography>
          ) : SubAccountBillingStatus.Removed === row.original.BillingStatus ? (
            <AppTypography color={'grey.500'}>Removed</AppTypography>
          ) : (
            <AppTypography color={'grey.500'}>{row.original.Status}</AppTypography>
          )}
        </>
      ),
    },
    {
      header: 'Name',
      cell: ({ row }) => (
        <AppTypography>{(row.original.FirstName ?? '') + ' ' + (row.original.LastName ?? '')}</AppTypography>
      ),
    },

    {
      header: 'Email',
      cell: ({ row }) => <EmailText email={row.original.Email} />,
    },
    {
      header: 'Data Added',
      cell: ({ row }) => <AppTypography color={'grey.500'}>{getFullDate(row.original.DateAdded)}</AppTypography>,
    },
    { header: 'Account Type', cell: () => <AppTypography color={'grey.500'}>Sub-Account</AppTypography> },
    {
      header: 'Billing Status',
      cell: ({ row }) => (
        <>
          {row.original.BillingStatus === SubAccountBillingStatus.Subscription_Removed ? (
            <AppTypography color={'grey.500'}>{IsPausedUser() ? 'Paused' : 'Removed'}</AppTypography>
          ) : (
            <AppTypography color={'grey.500'}>{row.original.BillingStatus}</AppTypography>
          )}
        </>
      ),
    },
    {
      header: 'Next Billing',
      cell: ({ row }) => (
        <AppTypography color={'grey.500'}>
          {row.original.BillingStatus === SubAccountBillingStatus.Billable &&
            getMDYFormattedDate(new Date(CurrentMembership()?.CurrentPeriodEnd ?? ''))}
        </AppTypography>
      ),
    },
  ]
  return <AppTable columns={columns} {...props} />
}
